import { AlertCircle, X, ChevronRight, Crown } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@mui/material";
import { SubscriptionLimitState } from "../../hooks/useSubscriptionLimit";

const limitTypeInfo: any = {
  projects: {
    title: "Project Creation Limit Reached",
    description: "You've reached your total project creation limit.",
    icon: <AlertCircle className="w-6 h-6 text-amber-500" />,
    upgradeFeatures: [
      { plan: "Growth", limit: "10 projects total" },
      { plan: "Pro", limit: "25 projects total" },
      { plan: "Scale", limit: "Unlimited projects" },
    ],
  },
  aiQuery: {
    title: "AI Query Limit Reached",
    description: "You've reached your monthly AI query limit.",
    icon: <AlertCircle className="w-6 h-6 text-amber-500" />,
    upgradeFeatures: [
      { plan: "Growth", limit: "200 queries/month" },
      { plan: "Pro", limit: "500 queries/month" },
      { plan: "Scale", limit: "Unlimited queries" },
    ],
  },
  icpGeneration: {
    title: "ICP Generation Limit Reached",
    description: "You've reached your monthly ICP generation limit.",
    icon: <AlertCircle className="w-6 h-6 text-amber-500" />,
    upgradeFeatures: [
      { plan: "Growth", limit: "10 icps/month" },
      { plan: "Pro", limit: "50 icps/month" },
      { plan: "Scale", limit: "100 icps/month" },
    ],
  },
  adStrategy: {
    title: "Ad Strategy Limit Reached",
    description: "You've reached your monthly ad strategy generation limit.",
    icon: <AlertCircle className="w-6 h-6 text-amber-500" />,
    upgradeFeatures: [
      { plan: "Growth", limit: "25 strategies/month" },
      { plan: "Pro", limit: "50 strategies/month" },
      { plan: "Scale", limit: "100 strategies/month" },
    ],
  },
  insights: {
    title: "Insights Report Limit Reached",
    description: "You've reached your monthly insights report limit.",
    icon: <AlertCircle className="w-6 h-6 text-amber-500" />,
    upgradeFeatures: [
      { plan: "Growth", limit: "10 reports/month" },
      { plan: "Pro", limit: "25 reports/month" },
      { plan: "Scale", limit: "Unlimited reports" },
    ],
  },
  adSpend: {
    title: "Ad Spend Limit Reached",
    description: "You've reached your monthly ad spend limit.",
    icon: <AlertCircle className="w-6 h-6 text-amber-500" />,
    upgradeFeatures: [
      { plan: "Growth", limit: "Up to $150k/month" },
      { plan: "Pro", limit: "Up to $300k/month" },
      { plan: "Scale", limit: "Unlimited spend" },
    ],
  },
};

interface SubscriptionLimitDialogProps {
  open: boolean;
  onClose: () => void;
  limitType: SubscriptionLimitState | any;
  current: number;
  limit: number;
  onUpgrade?: () => void;
}

const SubscriptionLimitDialog = ({
  open,
  onClose,
  limitType,
  current,
  limit,
  onUpgrade,
}: SubscriptionLimitDialogProps) => {
  const info = limitTypeInfo[limitType];
  if (!info) return <></>;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      classes={{ paper: "rounded-xl" }}
    >
      <div className="p-6">
        <div className="flex justify-between items-start mb-6">
          <DialogTitle className="p-0 text-xl font-semibold">
            <div className="flex items-center gap-3">
              {info.icon}
              <span>{info.title}</span>
            </div>
          </DialogTitle>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <DialogContent className="p-0">
          <div className="mb-6">
            <p className="text-gray-600 mb-4">{info.description}</p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <span className="text-amber-700">Current Usage</span>
                <span className="font-medium text-amber-900">
                  {current} / {limit}
                </span>
              </div>
              <div className="w-full bg-amber-200 rounded-full h-2 mt-2">
                <div
                  className="bg-amber-500 h-2 rounded-full transition-all duration-500"
                  style={{
                    width: `${Math.min((current / limit) * 100, 100)}%`,
                  }}
                />
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="font-medium text-gray-900 flex items-center gap-2">
              <Crown className="w-5 h-5 text-amber-500" />
              Upgrade to get more
            </h3>
            <div className="space-y-3">
              {info.upgradeFeatures.map((feature: any, index: number) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:border-blue-500 hover:bg-blue-50 transition-all cursor-pointer"
                  onClick={onUpgrade}
                >
                  <div>
                    <span className="font-medium text-gray-900">
                      {feature.plan}
                    </span>
                    <p className="text-sm text-gray-500">{feature.limit}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
            >
              Maybe Later
            </button>
            <button
              onClick={onUpgrade}
              className="ml-3 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Upgrade Now
            </button>
          </div>
        </DialogContent>
      </div>
    </Dialog>
  );
};

export default SubscriptionLimitDialog;
